import { FC } from 'react'
import gclasses from '../../../../../CRMSettings/global.module.css'
import classes from './index.module.css'
import cn from 'classnames'
import { LeadHistory } from '@/api/types/crm.types'
import {
  formatCallDuration,
  formatDate,
  formatDateTime,
  formatTime,
} from '../../../../utils/formatData'

interface HistoryItemProps {
  historyItem: LeadHistory
}

const HistoryItem: FC<HistoryItemProps> = ({ historyItem: item }) => {
  let additionalInfo: Record<string, any> | null = null
  if (item?.additional_info) {
    try {
      additionalInfo = JSON.parse(item.additional_info)
      if (typeof additionalInfo !== 'object' || additionalInfo === null) {
        console.warn(
          'additional_info parsed to invalid object:',
          item.additional_info,
        )
        additionalInfo = null
      }
    } catch (error) {
      console.error('Failed to parse additional_info:', error)
      additionalInfo = null
    }
  }

  const getAdditionalInfo = (parameter: string) => {
    if (!additionalInfo || !(parameter in additionalInfo)) return null
    return additionalInfo[parameter]
  }

  const Content = () => {
    switch (item.change_type) {
      case 'message':
        return <span>{item.change_description}</span>

      case 'lead_create':
        return (
          <>
            Лид "{getAdditionalInfo('name')}" ({getAdditionalInfo('phone')}) был
            создан
          </>
        )

      case 'lead_changes':
        const changes = getAdditionalInfo('changes')
        if (!Array.isArray(changes)) {
          return (
            <span>
              {getAdditionalInfo('message') ?? 'Изменения'}: Некорректные данные
            </span>
          )
        }
        const newValues = changes
          .filter((item) => item.new_value)
          .map((item) => `${item.field_name}: ${item.new_value}`)
        return (
          <>
            {getAdditionalInfo('message') ?? 'Изменения'} (
            {newValues.length > 0 ? newValues.join(', ') : 'Нет значений'})
          </>
        )

      case 'lead_import':
        return (
          <>
            Лид "{getAdditionalInfo('name')}" ({getAdditionalInfo('phone')}) был
            импортирован. Добавил: "
            {getAdditionalInfo('changed_by') ?? 'неизвестно'}"
          </>
        )

      case 'lead_has_duplicate':
        return (
          <>
            Удален дубль "{getAdditionalInfo('name')}" (
            {getAdditionalInfo('phone')}) дата следующего контакта обновлена.
            Добавил: "{getAdditionalInfo('changed_by') ?? 'неизвестно'}"
          </>
        )

      case 'add_status':
        return (
          <>
            Изменение статуса на "{getAdditionalInfo('status')}"
            <span
              className={classes.itemStatusColor}
              style={{ backgroundColor: getAdditionalInfo('color') }}
            />
          </>
        )

      case 'delete_status':
        return (
          <>
            Удаление статуса "{getAdditionalInfo('status')}"
            <span
              className={classes.itemStatusColor}
              style={{ backgroundColor: getAdditionalInfo('color') }}
            />
            <span>
              . Изменил: {getAdditionalInfo('changed_by') ?? 'неизвестно'}
            </span>
          </>
        )

      case 'add_date_l':
        const dateL = String(getAdditionalInfo('date'))
        return (
          <>
            Дата появления заявки:{' '}
            {dateL ? formatDateTime(dateL) : 'неизвестно'}
          </>
        )

      case 'add_date_z':
        const dateZ = String(getAdditionalInfo('date'))
        // prettier-ignore
        return `Дата взятия заявки: "${dateZ ? formatDateTime(dateZ) : 'неизвестно'}". Изменил: ${getAdditionalInfo('changed_by') ?? 'неизвестно'}`

      case 'add_date_n':
        const dateN = String(getAdditionalInfo('date'))
        return (
          <>
            Дата следующего контакта:{' '}
            {dateN ? formatDateTime(dateN) : 'неизвестно'}
          </>
        )

      case 'add_manager':
        // prettier-ignore
        return `Менеджер "${getAdditionalInfo('manager') ?? 'неизвестно'}" взял заявку. Изменил: ${getAdditionalInfo('changed_by') ?? 'неизвестно'}`

      case 'delete_manager':
        return (
          <>
            Менеджер "{getAdditionalInfo('manager') ?? 'неизвестно'}" снят с
            заявки
          </>
        )

      case 'sent_notification_by_button':
        return (
          <>Отправлено уведомление "{getAdditionalInfo('name')}" по кнопке</>
        )

      case 'sent_notification_by_trigger':
        return (
          <>Отправлено уведомление "{getAdditionalInfo('name')}" по триггеру</>
        )

      case 'call':
        const duration = getAdditionalInfo('duration')
        return (
          <>
            Совершён звонок длительностью в "
            {duration ? formatCallDuration(duration) : 'unknown'}"{' '}
            {/* FIXME: Add telephony support */}
            {/* <PhoneOutlined /> */}
          </>
        )

      case 'buy_course':
        return `Продан курс "${
          getAdditionalInfo('course_name') ?? 'неизвестно'
        }". Продал: ${getAdditionalInfo('changed_by') ?? 'неизвестно'}`

      case 'change_field': {
        const oldVal = getAdditionalInfo('old_value');
        const newVal = getAdditionalInfo('new_value');
        const isAutomation = getAdditionalInfo('is_automation');
        const fieldName = getAdditionalInfo('field') ?? 'unknown';

        return (
            <>
              Изменено поле "{fieldName}"{isAutomation === 1 ? ' (Автоматизация)' : ''}.
              {oldVal && ` Старое значение: "${oldVal}".`}
              {newVal && ` Новое значение: "${newVal}".`}
            </>
        );
      }

      case 'change_state':
        const oldState = getAdditionalInfo('old_state')
        // prettier-ignore
        return `Лид переведен ${oldState ? 'из состояния "' + String(oldState) + '"' : ''} в состояние "${getAdditionalInfo('new_state') ?? 'неизвестно'}". Изменил: ${getAdditionalInfo('changed_by') ?? 'неизвестно'}`

      case 'lead_add_payment_data':
        const paymentType = getAdditionalInfo('payment_type')
        const teacherName = getAdditionalInfo('teacher_name')
        const tariffName = getAdditionalInfo('tariff_name')
        const directionOrderName = getAdditionalInfo('direction_order_name')
        const groupName = getAdditionalInfo('group_name')
        const price = getAdditionalInfo('price')
        const classTitle = getAdditionalInfo('class_title')
        const userName = getAdditionalInfo('user_name')
        const paidFrom = getAdditionalInfo('paid_from')
        const paidTo = getAdditionalInfo('paid_to')
        const parts = [
          `Добавлен ${paymentType}:`,
          directionOrderName && `"${directionOrderName}"`,
          classTitle && `"${classTitle}"`,
          price && `за ${price} рублей`,
          paidFrom && `с "${paidFrom}" до "${paidTo}"`,
          tariffName && `Тариф "${tariffName}"`,
          groupName && `Группа "${groupName}"`,
          teacherName && `Педагог ${teacherName}`,
        ].filter(Boolean)

        return (
          <>
            {parts.join('. ')}.
            <br />
            Добавил: "{getAdditionalInfo('changed_by') ?? 'неизвестно'}"
          </>
        )

      default:
        return <span>Неизвестное действие</span>
    }
  }

  return (
    <div key={item.id} className={classes.item}>
      <div className={classes.itemDate}>
        <span className={classes.bold}>{formatDate(item.created_at)}</span>
        <span>{formatTime(item.created_at)}</span>
      </div>
      <div className={classes.itemContent}>
        <Content />
      </div>
    </div>
  )
}

export default HistoryItem
